import { UserService } from '@/services/users/UserService';
import { sanitizeUrlForAnalytics } from '@/lib/urlPrivacy';

// Activity Types - Comprehensive categorization of all possible activities
export const ACTIVITY_TYPES = {
  USER_ACTION: "user_action",
  SYSTEM_EVENT: "system_event",
  GENIE_INTERACTION: "genie_interaction",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  AUTHENTICATION: "authentication",
  ADMIN: "admin",
} as const;

// Activity Actions - Specific actions that can be performed
export const ACTIVITY_ACTIONS = {
  // General CRUD
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
  VIEWED: "viewed",

  // Authentication
  LOGGED_IN: "logged_in",
  LOGGED_OUT: "logged_out",
  PASSWORD_CHANGED: "password_changed",
  PROFILE_UPDATED: "profile_updated",

  // Genie Management
  GENIE_CREATED: "genie_created",
  GENIE_UPDATED: "genie_updated",
  GENIE_DELETED: "genie_deleted",
  GENIE_LAUNCHED: "genie_launched",
  GENIE_CONFIGURED: "genie_configured",
  GENIE_CATEGORY_CHANGED: "genie_category_changed",

  // Conversations
  CONVERSATION_STARTED: "conversation_started",
  CONVERSATION_ENDED: "conversation_ended",
  CONVERSATION_ANALYZED: "conversation_analyzed",
  CONVERSATION_EXPORTED: "conversation_exported",

  // Documents
  DOCUMENT_UPLOADED: "document_uploaded",
  DOCUMENT_PROCESSED: "document_processed",
  DOCUMENT_SHARED: "document_shared",

  // System Events
  DEPLOYMENT: "deployment",
  MAINTENANCE: "maintenance",
  ERROR_OCCURRED: "error_occurred",
  BACKUP_COMPLETED: "backup_completed",

  // Settings & Configuration
  SETTINGS_UPDATED: "settings_updated",
  EMAIL_CONFIGURED: "email_configured",
  PRONUNCIATION_ADDED: "pronunciation_added",
  QR_URL_CHANGED: "qr_url_changed",
  QR_SCANNED: "qr_scanned",

  // Engagement
  PAGE_VISITED: "page_visited",
  FEATURE_USED: "feature_used",
  TUTORIAL_COMPLETED: "tutorial_completed",
  SUITE_APP_VISITED: "suite_app_visited",

  // Team Management
  TEAM_MEMBER_INVITED: "team_member_invited",
  TEAM_CREATED: "team_created",
  TEAM_UPDATED: "team_updated",

  // Branding
  BRANDING_UPDATED: "branding_updated",
} as const;

// Resource Types - Types of resources that can be acted upon
export const RESOURCE_TYPES = {
  GENIE: "genie",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  USER: "user",
  USER_PROFILE: "user_profile",
  USER_ACCOUNT: "user_account",
  SYSTEM: "system",
  PAGE: "page",
  FEATURE: "feature",
  SETTINGS: "settings",
  TEAM: "team",
  TEAM_MANAGEMENT: "team_management",
} as const;

// Activity Categories - For organizing activities in the UI
export const ACTIVITY_CATEGORIES = {
  GENIE: "genie",
  CONVERSATION: "conversation",
  DOCUMENT: "document",
  ACCOUNT: "account",
  USER_MANAGEMENT: "user_management",
  SECURITY: "security",
  SYSTEM: "system",
  GENERAL: "general",
  SETTINGS: "settings",
} as const;

// Activity Priority Levels
export const ACTIVITY_PRIORITIES = {
  LOW: "low",
  NORMAL: "normal",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

// Activity Status
export const ACTIVITY_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const;

// Type definitions
export type ActivityType = (typeof ACTIVITY_TYPES)[keyof typeof ACTIVITY_TYPES];
export type ActivityAction = (typeof ACTIVITY_ACTIONS)[keyof typeof ACTIVITY_ACTIONS];
export type ResourceType = (typeof RESOURCE_TYPES)[keyof typeof RESOURCE_TYPES];
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[keyof typeof ACTIVITY_CATEGORIES];
export type ActivityPriority = (typeof ACTIVITY_PRIORITIES)[keyof typeof ACTIVITY_PRIORITIES];
export type ActivityStatus = (typeof ACTIVITY_STATUS)[keyof typeof ACTIVITY_STATUS];

export interface Activity {
  id: string;
  created_at: string;
  user_id?: string;
  session_id?: string;
  activity_type: ActivityType;
  action: ActivityAction;
  resource_type?: ResourceType;
  resource_id?: string;
  resource_name?: string;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  is_visible: boolean;
  priority: ActivityPriority;
  category: ActivityCategory;
  duration_ms?: number;
  status: ActivityStatus;
  user_full_name?: string;
  user_avatar_url?: string;
}

export interface LogActivityParams {
  activityType: ActivityType;
  action: ActivityAction;
  title: string;
  resourceType?: ResourceType;
  resourceId?: string;
  resourceName?: string;
  description?: string;
  metadata?: Record<string, any>;
  category?: ActivityCategory;
  priority?: ActivityPriority;
  isVisible?: boolean;
  durationMs?: number;
  status?: ActivityStatus;
}

export interface GetActivitiesParams {
  userId?: string;
  limit?: number;
  offset?: number;
  category?: ActivityCategory;
  activityType?: ActivityType;
  action?: string;
  startDate?: string;
  endDate?: string;
  resourceType?: ResourceType;
  resourceId?: string;
}

export class ActivityService {
  /**
   * Log a new activity
   */
  async logActivity(params: LogActivityParams): Promise<Activity | null> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();

      // Get browser context
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : undefined;
      const referrer = typeof document !== "undefined"
        ? sanitizeUrlForAnalytics(document.referrer)
        : undefined;

      const activityData = {
        user_id: user?.id,
        activity_type: params.activityType,
        action: params.action,
        resource_type: params.resourceType,
        resource_id: params.resourceId,
        resource_name: params.resourceName,
        title: params.title,
        description: params.description,
        metadata: params.metadata || {},
        user_agent: userAgent,
        referrer: referrer,
        is_visible: params.isVisible ?? true,
        priority: params.priority || ACTIVITY_PRIORITIES.NORMAL,
        category: params.category || ACTIVITY_CATEGORIES.GENERAL,
        duration_ms: params.durationMs,
        status: params.status || ACTIVITY_STATUS.COMPLETED,
      };

      const { ApiService } = await import('@/services/api/ApiService');
      const activity = await ApiService.invoke({
        resource: 'activities',
        action: 'create',
        data: activityData,
      });

      return activity as Activity;
    } catch (error) {
      console.error("Error in logActivity:", error);
      return null;
    }
  }

  /**
   * Helper methods for common activities
   */

  async logGenieLaunched(genieName: string, genieId: string) {
    return this.logActivity({
      activityType: ACTIVITY_TYPES.GENIE_INTERACTION,
      action: ACTIVITY_ACTIONS.GENIE_LAUNCHED,
      title: `Launched genie "${genieName}"`,
      resourceType: RESOURCE_TYPES.GENIE,
      resourceId: genieId,
      resourceName: genieName,
      category: ACTIVITY_CATEGORIES.GENIE,
    });
  }

  async logQrUrlChanged(genieName: string, genieId: string, oldUrl: string, newUrl: string) {
    return this.logActivity({
      activityType: ACTIVITY_TYPES.USER_ACTION,
      action: ACTIVITY_ACTIONS.QR_URL_CHANGED,
      title: `Changed QR URL for "${genieName}"`,
      description: `Updated URL from "${oldUrl}" to "${newUrl}"`,
      resourceType: RESOURCE_TYPES.GENIE,
      resourceId: genieId,
      resourceName: genieName,
      category: ACTIVITY_CATEGORIES.GENIE,
      priority: ACTIVITY_PRIORITIES.NORMAL,
      metadata: {
        oldUrl,
        newUrl,
        timestamp: new Date().toISOString(),
      },
    });
  }

  async logQrScanned(genieId: string, genieName: string, urlName: string, consumerId?: string | null) {
    const isMobile = typeof navigator !== "undefined" && /Mobi|Android/i.test(navigator.userAgent);
    const locale = typeof navigator !== "undefined" ? navigator.language : undefined;
    const languages = typeof navigator !== "undefined" ? [...navigator.languages] : undefined;
    const timezone = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
    const screen =
      typeof window !== "undefined"
        ? { width: window.screen.width, height: window.screen.height, pixelRatio: window.devicePixelRatio }
        : undefined;

    const { ApiService } = await import('@/services/api/ApiService');
    return ApiService.invoke(
      {
        resource: 'genies',
        action: 'log-qr-scanned',
        data: {
          genieId,
          genieName,
          urlName,
          consumerId: consumerId ?? null,
          device: isMobile ? "mobile" : "desktop",
          locale,
          languages,
          timezone,
          screen,
          timestamp: new Date().toISOString(),
        },
      },
      'public',
    );
  }

  async logSuiteAppVisit(appName: string, appUrl: string, source: string) {
    try {
      const {
        data: { user },
      } = await UserService.getUser();

      if (!user?.id) return null;

      const { LoggerService } = await import('@/services/logger/LoggerService');
      return LoggerService.logSuiteVisit({
        userId: user.id,
        appName,
        appUrl,
        source,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        referrer: typeof document !== "undefined"
          ? sanitizeUrlForAnalytics(document.referrer)
          : undefined,
      });
    } catch (error) {
      console.error("Error logging suite app visit:", error);
      return null;
    }
  }

  async getQrUrlChangeHistory(genieId: string, limit: number = 10): Promise<Activity[]> {
    // Use edge function for QR URL change history
    const { ApiService } = await import('@/services/api/ApiService');
    try {
      const response = await ApiService.invoke({
        resource: 'activities',
        action: 'all',
        data: {
          resourceId: genieId,
          action: ACTIVITY_ACTIONS.QR_URL_CHANGED,
          limit,
          offset: 0,
        },
      });
      return response?.activities || [];
    } catch (error) {
      console.error("Error fetching QR URL change history:", error);
      return [];
    }
  }
}

// Export singleton instance
export const activityService = new ActivityService();
